// ---------------------------------------------------------------------------
// Access Management API client — Batch A (Rounds 3 & 4, 2026).
//
// Wraps the role-management and role-permission endpoints so the Access
// Management page never has to know URL shapes or pluck .data from responses.
//
// Backend (all IT-Admin gated — [Authorize(Roles = "IT Admin")]):
//   Round 1 (RoleController):
//     GET    /api/Role                         → list roles (RoleId, RoleName, IsActive)
//     GET    /api/Role/active                  → active roles only
//     POST   /api/Role          { roleName }   → create (provisions ROLE:<id> group)
//     PUT    /api/Role          { roleId, roleName } → rename
//     DELETE /api/Role/:id                     → safe delete (409 if assigned / IT-Admin)
//     PATCH  /api/Role/:id/active { isActive } → enable / disable
//   Round 2 (AccessRolesController):
//     GET    /api/admin/access/roles/:id/permissions
//     PUT    /api/admin/access/roles/:id/permissions { permissionNames }
//     POST   /api/admin/access/roles/:id/permissions/:name
//     DELETE /api/admin/access/roles/:id/permissions/:name
//   Existing (UserPermissionsController) — reused for the catalog:
//     GET    /api/admin/user-permissions/catalog
//
// Error shape from the API on a refusal is { error: "..." }. Callers surface
// err.response.data.error to the user.
// ---------------------------------------------------------------------------
import api from "../api";

// ── Roles (Round 1) ─────────────────────────────────────────────────────────

/** All roles: [{ roleId, roleName, isActive }]. */
export const fetchRoles = async () => {
    const res = await api.get("/Role");
    return Array.isArray(res.data) ? res.data : [];
};

/** Active roles only — for the "assign role" picker (used by later batches). */
export const fetchActiveRoles = async () => {
    const res = await api.get("/Role/active");
    return Array.isArray(res.data) ? res.data : [];
};

/** Create a role. Backend provisions its ROLE:<id> permission group.
 *  Returns { roleId }. Throws on duplicate name (400). */
export const createRole = async (roleName) => {
    const res = await api.post("/Role", { roleName });
    return res.data; // { roleId }
};

/** Rename a role. Throws on duplicate name or IT-Admin rename (400). */
export const renameRole = async (roleId, roleName) => {
    await api.put("/Role", { roleId, roleName });
};

/** Enable/disable a role. Throws on disabling IT-Admin (409). */
export const setRoleActive = async (roleId, isActive) => {
    const res = await api.patch(`/Role/${roleId}/active`, { isActive });
    return res.data; // { roleId, isActive }
};

/** Safe-delete a role. Throws 409 if assigned to employees or if IT-Admin. */
export const deleteRole = async (roleId) => {
    await api.delete(`/Role/${roleId}`);
};

// ── Role permissions (Round 2) ───────────────────────────────────────────────

/** Full catalog flagged for this role:
 *  { roleId, roleName, isActive, groupName,
 *    permissions: [{ permissionName, description, assigned }],
 *    assignedPermissionNames: [...] }.
 *  Returns null if the role isn't found. */
export const fetchRolePermissions = async (roleId) => {
    try {
        const res = await api.get(`/admin/access/roles/${roleId}/permissions`);
        return res.data;
    } catch (err) {
        if (err?.response?.status === 404) return null;
        throw err;
    }
};

/** Replace a role's ENTIRE permission set (transactional diff on the server).
 *  Returns { added, removed, role }. Throws 400 if any name isn't in catalog. */
export const replaceRolePermissions = async (roleId, permissionNames) => {
    const res = await api.put(
        `/admin/access/roles/${roleId}/permissions`,
        { permissionNames }
    );
    return res.data;
};

/** Add one permission to a role (idempotent). Returns { added, role }. */
export const addRolePermission = async (roleId, permissionName) => {
    const res = await api.post(
        `/admin/access/roles/${roleId}/permissions/${encodeURIComponent(permissionName)}`
    );
    return res.data;
};

/** Remove one permission from a role (idempotent). Returns { removed, role }. */
export const removeRolePermission = async (roleId, permissionName) => {
    const res = await api.delete(
        `/admin/access/roles/${roleId}/permissions/${encodeURIComponent(permissionName)}`
    );
    return res.data;
};

// ── Catalog (reused existing endpoint) ───────────────────────────────────────

/** All permissions defined in SSW_Dashboard_Permissions:
 *  [{ permissionName, description }]. */
export const fetchPermissionCatalog = async () => {
    const res = await api.get("/admin/user-permissions/catalog");
    return Array.isArray(res.data) ? res.data : [];
};

// ── Employee role assignment (Batch B / Round 5) ─────────────────────────────
// Reuses the existing EmployeeRoleAdminController endpoints (Round 1 added the
// lockout guard on assign). These are the same endpoints the standalone
// /admin/employee-roles page uses — we deliberately share them.

/** Employees with their current role:
 *  [{ empId, name, firstName, lastName, company, jcDept, empStatus, roleId, roleName }]. */
export const fetchEmployeesWithRoles = async () => {
    const res = await api.get("/admin/employees-with-roles");
    return Array.isArray(res.data) ? res.data : [];
};

/** Assign or clear an employee's role. Pass null to clear.
 *  Returns the updated { roleId, roleName }. Throws 400 on the last-IT-Admin
 *  / disabled-role guards (Round 1). */
export const assignEmployeeRole = async (empId, roleId) => {
    const res = await api.put(
        `/admin/employees/${encodeURIComponent(empId)}/role`,
        { roleId }
    );
    return res.data;
};

// ── Direct user permissions (Batch B / Round 6) ──────────────────────────────
// Reuses the existing UserPermissionsController endpoints (Round 1 added the
// grant/revoke audit). Same endpoints as the standalone /admin/user-permissions
// page.

/** List of employees for the user-picker:
 *  [{ empId, name, department, role, roleId }]. */
export const fetchPermissionEmployees = async () => {
    const res = await api.get("/admin/user-permissions/employees");
    return Array.isArray(res.data) ? res.data : [];
};

/** Effective permissions for one employee, tagged by source
 *  (Direct / Role / Department). Returns null if not found.
 *  Shape: { employee: {...}, permissions: [{ permissionName, description, source }] }. */
export const fetchUserPermissionSnapshot = async (empId) => {
    try {
        const res = await api.get(`/admin/user-permissions/${encodeURIComponent(empId)}`);
        return res.data;
    } catch (err) {
        if (err?.response?.status === 404) return null;
        throw err;
    }
};

/** Grant a permission directly to a user. Returns the fresh snapshot. */
export const grantUserPermission = async (empId, permissionName) => {
    const res = await api.post(
        `/admin/user-permissions/${encodeURIComponent(empId)}`,
        { permissionName }
    );
    return res.data;
};

/** Revoke a DIRECT-source permission. Returns the fresh snapshot. */
export const revokeUserPermission = async (empId, permissionName) => {
    const res = await api.delete(
        `/admin/user-permissions/${encodeURIComponent(empId)}/${encodeURIComponent(permissionName)}`
    );
    return res.data;
};

/** DENY a permission for one user — removes it even if their role grants it
 *  (role-default + removable-per-person model). Returns the fresh snapshot. */
export const denyUserPermission = async (empId, permissionName) => {
    const res = await api.post(
        `/admin/user-permissions/${encodeURIComponent(empId)}/deny/${encodeURIComponent(permissionName)}`
    );
    return res.data;
};

/** RESTORE a denied permission (remove the deny marker). Returns fresh snapshot. */
export const restoreUserPermission = async (empId, permissionName) => {
    const res = await api.delete(
        `/admin/user-permissions/${encodeURIComponent(empId)}/deny/${encodeURIComponent(permissionName)}`
    );
    return res.data;
};

// ── Effective access preview (Batch B / Round 7) ─────────────────────────────

/** Full EffectiveAccessBundle for ANY employee (admin preview).
 *  Includes identity, role, permissions, flags, and the resolved modules map.
 *  Endpoint is [Authorize(Policy = CanAccessAdmin)]. Returns null if not found.
 *  Shape: { empId, name, role, roleId, department, country, permissions: [...],
 *           isItAdmin, isManager, ..., modules: { "dept.hr": true, ... } }. */
export const fetchEffectiveAccess = async (empId) => {
    try {
        const res = await api.get(`/Permissions/effective/${encodeURIComponent(empId)}`);
        return res.data;
    } catch (err) {
        if (err?.response?.status === 404) return null;
        throw err;
    }
};
